import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  CheckCircle2, 
  Circle, 
  Clock, 
  Briefcase, 
  Home,
  ChevronRight,
  Shield
} from 'lucide-react';
import { KanbanTask, Role } from '@/types';
import { clsx } from 'clsx';

interface TaskPulseProps {
  tasks: KanbanTask[];
  currentUserId: string;
  currentHouseholdId: string | null;
  userRole?: Role;
}

export const TaskPulse: React.FC<TaskPulseProps> = ({ 
  tasks, 
  currentUserId, 
  currentHouseholdId 
}) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<'all' | 'maintenance' | 'action' | 'grocery'>('all');
  
  const activeTasks = tasks
    .filter(task => {
      const isActive = task.status === 'todo' || task.status === 'in_progress';
      const matchesFilter = filter === 'all' || task.templateType === filter;
      
      const isPersonal = (task as any).source === 'personal';
      
      if (isPersonal && task.ownerId !== currentUserId) return false;

      return isActive && matchesFilter && !task.isDeleted;
    })
    .sort((a, b) => {
      const priorityMap = { P1: 0, P2: 1, P3: 2, none: 3 };
      return (priorityMap[a.priorityTier ?? 'none']) - (priorityMap[b.priorityTier ?? 'none']);
    })
    .slice(0, 6);

  const getPriorityStyle = (tier: string, source: string) => {
    if (source === 'personal') return 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800';
    switch (tier) {
      case 'P1': return 'text-rose-500 bg-rose-50 dark:bg-rose-900/20 border-rose-100 dark:border-rose-800';
      case 'P2': return 'text-amber-500 bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800';
      case 'P3': return 'text-brand bg-indigo-50 dark:bg-indigo-900/20 border-indigo-100 dark:border-indigo-800';
      default: return 'text-slate-400 bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700';
    }
  };

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* --- FILTER BAR --- */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
        {(['all', 'action', 'maintenance', 'grocery'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={clsx(
              "px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all whitespace-nowrap",
              filter === f 
                ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-transparent shadow-md' 
                : 'bg-white dark:bg-slate-900 text-slate-400 border-slate-100 dark:border-slate-800 hover:border-indigo-200'
            )}
          >
            {f}
          </button>
        ))}
      </div>

      {/* --- TASK LIST --- */}
      <div className="flex-1 space-y-2 overflow-y-auto no-scrollbar">
        {activeTasks.length > 0 ? (
          activeTasks.map((task) => {
            const isPersonal = (task as any).source === 'personal';
            return (
              <div 
                key={task.id} 
                className="group flex items-center justify-between p-3 bg-white dark:bg-slate-900 border border-slate-50 dark:border-slate-800 rounded-2xl hover:border-indigo-200 dark:hover:border-indigo-800 transition-all cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={clsx(
                    "shrink-0 w-2 h-2 rounded-full",
                    task.priorityTier === 'P1' ? 'bg-rose-500 animate-pulse' : isPersonal ? 'bg-emerald-400' : 'bg-slate-200 dark:bg-slate-700'
                  )} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h4 className="text-[11px] font-bold text-slate-800 dark:text-slate-100 truncate leading-tight uppercase italic">
                        {task.title}
                      </h4>
                      {isPersonal && <Shield className="w-2.5 h-2.5 text-emerald-500/50" />}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={clsx(
                        "text-[8px] font-black px-1.5 py-0.5 rounded-md border uppercase tracking-tighter",
                        getPriorityStyle(task.priorityTier ?? 'none', (task as any).source)
                      )}>
                        {isPersonal ? 'Private' : (task.priorityTier === 'none' ? 'Task' : task.priorityTier)}
                      </span>
                      {task.energyRequired && (
                        <span className="text-[8px] font-bold text-slate-400 flex items-center gap-0.5">
                          ⚡ {task.energyRequired}/10
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-slate-200 group-hover:text-brand transition-colors" />
              </div>
            );
          })
        ) : (
          <div className="h-full flex flex-col items-center justify-center opacity-20 grayscale py-8">
            <CheckCircle2 className="w-8 h-8 mb-2" />
            <p className="text-[9px] font-black uppercase tracking-widest">{t('tasks.protocol_clear', 'Protocol Clear')}</p>
          </div>
        )}
      </div>

      {/* --- FOOTER PROGRESS --- */}
      <div className="pt-2 border-t border-slate-50 dark:border-slate-800 flex items-center justify-between">
        <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">
          Active Tasks • {activeTasks.length} Active
        </span>
        <div className="w-20 h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
          <div 
            className={clsx(
              "h-full transition-all duration-1000 bg-brand"
            )} 
            style={{ width: `${(activeTasks.length / 10) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};